import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { adGroupTemplateRouteFactory } from "./adGroupTemplate";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import { setupTemplateViewLoader, setupTemplateFormLoader } from "../loaders/setupTemplate";
import { adGroupTemplateListBySetupTemplateLoader } from "../loaders/adGroupTemplate";
import { SetupTemplateBreadcrumb } from "../../breadcrumbs/setupTemplate";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityCreateLoading from "../../pages/entities/loading/EntityCreateLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";

const { SETUPTEMPLATE, ADGROUPTEMPLATE } = ENTITY;

export const setupTemplateRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const setupTemplateRoute = createRoute({
    getParentRoute: () => parent,
    path: SETUPTEMPLATE.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: SETUPTEMPLATE.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: SETUPTEMPLATE.label.upFirstPlural,
    },
  });

  // List setupTemplates

  const setupTemplateListRoute = createRoute({
    getParentRoute: () => setupTemplateRoute,
    path: "/",
    pendingComponent: () => <EntityListLoading entity={SETUPTEMPLATE} />,
  }).lazy(() => import("./setupTemplate.lazy").then((d) => d.SetupTemplateListRoute));

  // Create setupTemplate

  const setupTemplateCreateRoute = createRoute({
    getParentRoute: () => setupTemplateRoute,
    path: "create",
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: SETUPTEMPLATE.type.singular, action: "CREATE" },
      ]),
    staticData: {
      breadcrumb: "Create new",
    },
    loader: setupTemplateFormLoader,
    pendingComponent: () => <EntityCreateLoading entity={SETUPTEMPLATE} />,
  }).lazy(() => import("./setupTemplate.lazy").then((d) => d.SetupTemplateCreateRoute));

  // View setupTemplate

  const setupTemplateViewRootRoute = createRoute({
    getParentRoute: () => setupTemplateRoute,
    path: "$setupTemplateId",
    staticData: {
      breadcrumb: SetupTemplateBreadcrumb,
    },
    loader: setupTemplateViewLoader,
  });

  // Edit setupTemplate

  const setupTemplateEditRoute = createRoute({
    getParentRoute: () => setupTemplateViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: setupTemplateFormLoader,
    pendingComponent: () => <EntityEditLoading entity={SETUPTEMPLATE} />,
  }).lazy(() => import("./setupTemplate.lazy").then((d) => d.SetupTemplateEditRoute));

  // View setupTemplate Layout

  const setupTemplateViewLayoutRoute = createRoute({
    getParentRoute: () => setupTemplateViewRootRoute,
    id: "setupTemplate-view-layout",
    pendingComponent: () => <EntityViewLoading entity={SETUPTEMPLATE} />,
  }).lazy(() =>
    import("./setupTemplate.lazy").then((d) => d.SetupTemplateViewLayoutRoute)
  );

  // View setupTemplate - General Tab

  const setupTemplateViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => setupTemplateViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./setupTemplate.lazy").then((d) => d.SetupTemplateViewLayoutGeneralTabRoute)
  );

  // View setupTemplate - About Tab

  const setupTemplateViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => setupTemplateViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./setupTemplate.lazy").then((d) => d.SetupTemplateViewLayoutAboutTabRoute)
  );

  // View setupTemplate - AdGroupTemplates Tab

  const setupTemplateViewLayoutAdGroupTemplateListTabRoute = createRoute({
    getParentRoute: () => setupTemplateViewLayoutRoute,
    path: `${ADGROUPTEMPLATE.type.plural}/`,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADGROUPTEMPLATE.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: ADGROUPTEMPLATE.label.upFirstPlural,
    },
    loader: adGroupTemplateListBySetupTemplateLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./setupTemplate.lazy").then(
      (d) => d.SetupTemplateViewLayoutAdGroupTemplateListTabRoute
    )
  );

  // SetupTemplate Children
    const adGroupTemplateRoute = adGroupTemplateRouteFactory(
      setupTemplateViewRootRoute
    );

  // Route tree
  setupTemplateRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [setupTemplateListRoute]),
    setupTemplateCreateRoute,
    setupTemplateViewRootRoute.addChildren([
      setupTemplateEditRoute,
      setupTemplateViewLayoutRoute.addChildren([
        setupTemplateViewLayoutGeneralTabRoute,
        setupTemplateViewLayoutAboutTabRoute,
        setupTemplateViewLayoutAdGroupTemplateListTabRoute
      ]),
      adGroupTemplateRoute
    ]),
  ]);

  return setupTemplateRoute;
};

const setupTemplateRoute = setupTemplateRouteFactory();

export default setupTemplateRoute;
